import { CollectionsEmpty } from '@/components/features/Collection/CollectionsGrid/CollectionsEmpty';
import { FormatCryptoCurrency } from '@/components/primitives';
import { CollectionCard } from '@/components/shared/CollectionCard';
import { CollectionType } from '@/hooks/use-collections';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { routes } from '@/utils/routes';
import { Flex, SimpleGrid, SimpleGridProps, Text } from '@chakra-ui/react';
import { formatNumber } from '@onbeam/utils';
import useTranslation from 'next-translate/useTranslation';
import { type FC } from 'react';

export const COLLECTIONS_PER_PAGE = 36;

type Props = {
  collections?: CollectionType[];
} & SimpleGridProps;

const CollectionsGrid: FC<Props> = ({ collections = [], ...props }) => {
  const { t } = useTranslation('collection-overview');
  const chain = useMarketplaceChain();

  if (!collections.length)
    return (
      <CollectionsEmpty
        label={t('empty')}
        columns={props.columns as Partial<Record<string, number>>}
      />
    );

  return (
    <SimpleGrid
      w="full"
      columns={{ base: 1, md: 2, lg: 3, '2xl': 4 }}
      spacing="space.24"
      {...props}
    >
      {collections.map(collection => (
        <CollectionCard
          key={collection.id}
          mb="space.12"
          image={{
            src: collection.banner || collection.image,
            alt: collection.name,
          }}
          link={{
            href: routes.collections.detail(collection.id ?? '', chain.routePrefix),
          }}
          creator={{
            avatar: collection.image,
            address: collection.creator,
          }}
          title={collection.name}
          description={collection.description}
        >
          <Flex gap="space.18" alignItems="flex-end" flexGrow={1}>
            {!!collection.tokenCount && (
              <Flex direction="column" gap="space.2">
                <Text color="gray.6" fontSize="xs">
                  {t('collection-card.items')}
                </Text>
                <Text color="gray.1" fontWeight="medium" fontSize="sm">
                  {formatNumber(collection.tokenCount)}
                </Text>
              </Flex>
            )}
            {!!collection.ownerCount && (
              <Flex direction="column" gap="space.2">
                <Text color="gray.6" fontSize="xs">
                  {t('collection-card.owners')}
                </Text>
                <Text color="gray.1" fontWeight="medium" fontSize="sm">
                  {formatNumber(collection.ownerCount)}
                </Text>
              </Flex>
            )}
            {!!collection.floorAsk?.price?.amount?.decimal && (
              <Flex direction="column" gap="space.2">
                <Text color="gray.6" fontSize="xs">
                  {t('collection-card.floor-price')}
                </Text>
                <FormatCryptoCurrency
                  amount={collection.floorAsk.price.amount.decimal}
                  address={collection.floorAsk.price.currency?.contract}
                  decimals={collection.floorAsk.price.currency?.decimals}
                  symbol={collection.floorAsk.price.currency?.symbol}
                  logoHeight={14}
                  fontWeight="medium"
                  fontSize="sm"
                  color="gray.1"
                />
              </Flex>
            )}
            {!!collection.volume?.allTime && (
              <Flex direction="column" gap="space.2">
                <Text color="gray.6" fontSize="xs">
                  {t('collection-card.volume')}
                </Text>
                <FormatCryptoCurrency
                  amount={collection.volume.allTime}
                  logoHeight={14}
                  fontWeight="medium"
                  fontSize="sm"
                  color="gray.1"
                />
              </Flex>
            )}
          </Flex>
        </CollectionCard>
      ))}
    </SimpleGrid>
  );
};

export default CollectionsGrid;
